import type { ReactNode } from 'react';

/** Paper thumbnail with file type (design/Heizkosten ista PDF, BelegUpload queue, SteuerExport formats). */
export const FileBadge = ({ ext, size = 'md', accent }: { ext: string; size?: 'sm' | 'md' | 'lg'; accent?: boolean }) => (
  <span className={`file-badge ${size} ${accent ? 'accent' : ''}`} aria-hidden>
    <span className="fb-lines" />
    <span className="fb-ext">{ext}</span>
  </span>
);

/**
 * Two (or more) part bar with labels inside or below (design/Heizkosten "Grund 30 % · Verbrauch 70 %",
 * design/CO2 "Vermieter 50 % · Mieter 50 %"). `share` values are relative weights; tones from tokens.
 */
export function SplitBar({ parts, label, size = 'md' }: { parts: { share: number; label?: ReactNode; tone: 'primary' | 'accent' | 'muted' | 'warn' }[]; label: string; size?: 'sm' | 'md' | 'lg' }) {
  const total = parts.reduce((s, p) => s + Math.max(0, p.share), 0) || 1;
  return (
    <div className={`split-bar ${size}`} role="img" aria-label={label}>
      {parts.map((p, i) => (
        <span key={i} className={`sb-part ${p.tone}`} style={{ width: `${(Math.max(0, p.share) / total) * 100}%` }}>
          {p.label != null && size !== 'sm' && <span className="sb-label">{p.label}</span>}
        </span>
      ))}
    </div>
  );
}

export type MonthState = 'covered' | 'manual' | 'review' | 'missing' | 'expected' | 'na' | 'empty';
/** Twelve month squares of a cost type (design/Checkliste "Jan – Dez"). The row text carries the status too. */
export function MonthStrip({ months, label }: { months: MonthState[]; label: string }) {
  return (
    <span className="month-strip" role="img" aria-label={label}>
      {months.map((m, i) => <span key={i} className={`ms-${m}`} />)}
    </span>
  );
}

/** Legend item for MonthStrip and similar swatches. */
export const Swatch = ({ state, children }: { state: MonthState; children: ReactNode }) => (
  <span className="swatch"><span className={`swatch-box ms-${state}`} aria-hidden />{children}</span>
);

/**
 * Chain of calculation tiles "input → result" (design/CO2 "So kommt die Zahl zustande").
 * `highlight` marks the decisive tile.
 */
export function CalcChain({ items, label }: { items: { label: ReactNode; value: ReactNode; sub?: ReactNode; highlight?: boolean }[]; label: string }) {
  return (
    <dl className="calc-chain" aria-label={label}>
      {items.map((c, i) => (
        <div key={i} className={c.highlight ? 'hl' : undefined}>
          <dt>{c.label}</dt>
          <dd>
            <span className="cc-value">{c.value}</span>
            {c.sub && <span className="cc-sub">{c.sub}</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}
